/**
 * Which content blocks an article can render, and how.
 *
 * THIS FILE IS THE SOURCE OF TRUTH for "does this block exist". The knowledge base's
 * `tools/block_registry.json` is generated from it, and the exporter marks a block
 * `implemented: false` when it is absent here. Keeping the answer in the dispatch map
 * itself means it cannot drift from what actually renders — a block is implemented
 * exactly when there is something in `BLOCK_COMPONENTS` to render it.
 *
 * WHY A MISSING BLOCK IS NOT AN ERROR
 * Decided 2026-08-25. `blog-content-blocks.md` catalogues 20 blocks; 12 exist, most of
 * them embedded inside a guide-page section rather than exported, and the rest are
 * proposals. Making an article wait on the frontend ticket for its block would mean
 * Ilse's approval is not the last gate after all. So an unimplemented block renders
 * `UnimplementedBlock` — the fallback prose the exporter wrote, plus a console warning
 * and a `data-` attribute, both aimed at us and neither visible to a reader.
 *
 * Extracting the remaining blocks out of `Budget`, `Food`, `Practicalities`,
 * `Destinations` and `Culture` is the natural next batch: they are already built, just
 * not reachable from anything but a guide page. See work/blog-content-pipeline.md.
 */

import trackEvent from "components/tracking/trackEvent";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { routePreviewUrlIn, withRoutePreviewLang, withUtmContent } from "components/website/pages/articleTripHref";
import { buildLocalizedPath, hasLocalisedRoute, isMarketingLangCode, localizeHref, type MarketingLangCode } from "i18n/urlLocale";
import { useEffect } from "react";
import type { ArticleBlock } from "./articleData";
import type { CityData } from "./cityData";
import type { GuideIconName } from "./discoverSections";
import {
	AIPlanBanner,
	BudgetTiers,
	DoDont,
	EsimPackages,
	ExploreMore,
	PhotoCallout,
	ReadMore,
	SaveWidget,
	StatsStrip,
	StayWidget,
	TipCallout,
	TransportTable,
	TrustFooter,
	WeatherByMonth,
	WhereToStay,
	fillUi,
	useGuideUi,
} from "./discoverSections";

/** What every block component receives. `data` is inlined by the exporter. */
interface BlockProps {
	props: Record<string, string>;
	data: Record<string, any> | null;
	/**
	 * The article's own locale, never derived from the URL or `window.location` — `renderBlock`
	 * runs during SSR too, before there is a `window`. Optional (defaults to "en" in
	 * `localizeHref` below) so the many blocks that never render a path-carrying href, and the
	 * direct-construction tests in `blockRegistryHidden.test.tsx` / `blockRegistry.test.tsx`,
	 * don't need to pass it. `renderBlock` always supplies the real value.
	 */
	lang?: MarketingLangCode;
}

/**
 * Localizes an href carried in a block's `props`.
 *
 * These hrefs come straight off the knowledge base (`tools/build_article_documents.py`,
 * `_section_geo`'s docstring), which deliberately does not translate attributes so an href
 * like `/discover/thailand/bangkok` is byte-identical across all seven locales' copies of an
 * article. Rendered as-is that has no locale of its own, so a German reader following it off
 * `/de/blog/...` lands back in English — the reported bug.
 *
 * Only a bare internal path (`/discover/...`) is a candidate: an absolute URL, `mailto:`, an
 * in-page anchor (`#...`), or a protocol-relative `//host/...` link is left completely alone —
 * none of those start with a single `/`. And only a path that actually resolves to a
 * locale-routed page gets prefixed (`hasLocalisedRoute`, whose doc comment holds the full list)
 * — the same gate `Navigation.tsx` and `FeatureMenu.tsx` apply. Bare `/discover` used to be the
 * one internal path that gate deliberately excluded; 123yb6937h0 gave the hub its own per-locale
 * pages, so it is now prefixed like everything else.
 */

// `data.months[]` also carries `note` — the dossier's free-form prose per month
// (`build_guide_documents.py`, `r.get("Note", "")`) — which `WeatherByMonth` does not read and
// this does not pass down. Left out deliberately rather than by oversight: `note` is not in
// `exploreTranslation.ts`'s translation allowlist (`guide.months` is excluded entirely, unlike
// `guide.budget`), so a geo's `note` strings have never been through the paid translation
// pipeline, and rendering them would mean shipping raw English prose disguised as a localised
// month strip. If a future design wants `note` on the page, it has to go through that pipeline
// first — see the daily-budget fix (123yb693tje) in marketingSsrLoaders.ts for the read-time
// overlay pattern a translated version of this would follow.
const WeatherBlock = ({ props, data }: BlockProps) => <WeatherByMonth months={data?.months || []} highlight={props.highlight} />;

const StatStripBlock = ({ props, data }: BlockProps) => {
	const ui = useGuideUi();
	return data?.items?.length ? <StatsStrip items={data.items} label={props.label || ui("blog.section.stipplData", "Stippl data")} /> : null;
};

const EditorialTipBlock = ({ props }: BlockProps) => {
	const ui = useGuideUi();
	return props.text ? <TipCallout label={props.label || ui("blog.section.editorsTip", "Editor's tip")}>{props.text}</TipCallout> : null;
};

const TransportTableBlock = ({ data }: BlockProps) => (data?.rows?.length ? <TransportTable rows={data.rows} /> : null);

// The one block whose props are all required by the component, so a half-specified
// directive renders nothing rather than an eSIM card with blank fields.
const EsimPricingBlock = ({ props, data }: BlockProps) => {
	const ui = useGuideUi();
	return data?.packages?.length && props.countryCode && props.countryLabel ? (
		<EsimPackages
			countryCode={props.countryCode}
			countryLabel={props.countryLabel}
			packages={data.packages}
			note={props.note || ""}
			ctaLabel={props.ctaLabel || ui("blog.section.esimSeeAll", "See all packages")}
		/>
	) : null;
};

// Three of these were built all along, just not reachable from anything but a guide page —
// which is how one article ended up printing four identical fallback lines. `ai-planner` and
// `budget-tracker` needed no extraction at all; `daily-budget` needed `BudgetTiers` pulled out
// of the section heading and breakdown table the guide wraps it in.
const AiPlannerBlock = ({ props }: BlockProps) =>
	props.destination ? <AIPlanBanner name={props.destination} onPlan={() => window.location.assign("/ai-travel-planner")} /> : null;

const BudgetTrackerBlock = () => {
	const ui = useGuideUi();
	return (
		<SaveWidget
			icon="budget"
			title={ui("blog.block.budgetTracker.title", "Track what you actually spend")}
			body={ui("blog.block.budgetTracker.body", "Set a daily budget per country and log expenses in any currency while you travel.")}
			cta={ui("blog.block.budgetTracker.cta", "Try the budget tracker")}
			tone="amber"
			onClick={() => window.location.assign("/budget-planner")}
		/>
	);
};

const DailyBudgetBlock = ({ data }: BlockProps) => (data?.budget?.tiers?.length ? <BudgetTiers tiers={data.budget.tiers} /> : null);

// ── The rest, wired 2026-08-25 ────────────────────────────────────────────────
//
// Most of these needed no new component at all: `esim-banner`, `discover-food`, `route-cta`,
// `save-poi` and `activity-cta` are all `SaveWidget` invocations that already existed inside a
// guide section, and `seasonal-cta` is `PhotoCallout`. Only `do-dont` needed extracting
// (`DoDont`, out of `Culture`) and only `trust-footer` was genuinely new.
//
// Deliberately not wired: `save-to-trip` (no design exists — proposed by analogy, never
// screenshotted). Robin's call, 2026-08-25. `hotel-picks` and `explore-more` were the other
// two on that list; both got a design and are wired below (123yb693pet / 123yb692y1f,
// design confirmed by Ilse 2026-09-16).

const EsimBannerBlock = ({ props }: BlockProps) => {
	const ui = useGuideUi();
	const where = props.destination || ui("blog.block.esimBanner.where", "your destination");
	return (
		<SaveWidget
			icon="esim"
			title={fillUi(ui("blog.block.esimBanner.title", "Land in {where} already online"), { where })}
			body={ui("blog.block.esimBanner.body", "Install a data eSIM before you fly: no airport queue, no swapping cards.")}
			cta={fillUi(ui("blog.block.esimBanner.cta", "See {where} eSIMs"), { where })}
			tone="primary"
			onClick={() => window.location.assign("/esim")}
		/>
	);
};

/**
 * `discover-food` and `discover-bars` differ only in words — one component, two entries.
 *
 * Both used to navigate straight to the dead `/discover` browse index; 123yb691zpg's rule
 * covers this block too, so the click now opens the signup modal in place instead.
 */
const discoverBlock = (icon: GuideIconName, titleKey: string, titleFallback: string, bodyKey: string, bodyFallback: string) =>
	function DiscoverBlock() {
		const ui = useGuideUi();
		const gate = usePlanGate("blog_discover_cta", "store");
		return (
			<>
				<SaveWidget
					icon={icon}
					title={ui(titleKey, titleFallback)}
					body={ui(bodyKey, bodyFallback)}
					cta={ui("blog.block.discoverCta", "Explore in Stippl")}
					tone="purple"
					onClick={gate.requestPlan}
				/>
				<PlanGateModal gate={gate} title={ui("blog.block.discoverCta.modalTitle", "Create a free account to save these spots")} />
			</>
		);
	};

// `/route-planner` still serves the generic SPA shell rather than a real page (re-checked
// 2026-08-25). Robin's call, 2026-08-25: ship the block against `/discover`, the nearest real
// page. 2026-09-05 (123yb691zpg): the reader is being invited to plan, so the click should open
// the signup modal in place rather than navigate at all — same rule as every other plan CTA.
// The modal lives inside the block itself because an article renders many blocks side by side.
// 2026-10-08 (Ilse): a block whose `href` is the article's own trip (`/routepreview/<tripId>`)
// links straight to it instead — a real route beats a signup wall. Any other `href`, including
// the old name-based `/stippl_/trip/<name>` form, keeps the modal (see `routePreviewUrlIn`).
const RouteCtaBlock = ({ props, lang }: BlockProps) => {
	const ui = useGuideUi();
	const gate = usePlanGate("blog_route_cta", "store");
	const tripHref = props.href ? routePreviewUrlIn(props.href) : null;
	const title = props.destination
		? fillUi(ui("blog.block.routeCta.titleWithWhere", "Turn these stops into a {where} route"), { where: props.destination })
		: ui("blog.block.routeCta.title", "Turn these stops into a route");
	return (
		<>
			<SaveWidget
				icon="route"
				title={title}
				body={ui("blog.block.routeCta.body", "Pick your favourites and Stippl orders them, adds travel times and builds the day-by-day.")}
				cta={ui("blog.block.routeCta.cta", "Build my route")}
				tone="primary"
				href={tripHref ? withUtmContent(withRoutePreviewLang(tripHref, lang ?? "en"), "route_cta") : undefined}
				onClick={gate.requestPlan}
			/>
			<PlanGateModal gate={gate} title={ui("blog.block.routeCta.modalTitle", "Create a free account to build your route")} />
		</>
	);
};

// Used to navigate straight to the dead `/discover` browse index; now opens the signup modal
// in place instead, same rule as every other plan CTA (123yb691zpg).
const SavePoiBlock = ({ props }: BlockProps) => {
	const ui = useGuideUi();
	const gate = usePlanGate("blog_save_poi", "store");
	return props.place ? (
		<>
			<SaveWidget
				icon="heritage"
				title={fillUi(ui("blog.block.savePoi.title", "Save {place} to your trip"), { place: props.place })}
				cta={ui("blog.block.savePoi.cta", "Save")}
				tone="primary"
				onClick={gate.requestPlan}
			/>
			<PlanGateModal gate={gate} title={ui("blog.block.savePoi.modalTitle", "Create a free account to save this to your trip")} />
		</>
	) : null;
};

// Same conversion as SavePoiBlock above: this used to navigate to the dead `/discover` browse
// index, and now opens the signup modal in place instead.
const ActivityCtaBlock = ({ props }: BlockProps) => {
	const ui = useGuideUi();
	const gate = usePlanGate("blog_activity_cta", "store");
	const where = props.destination;
	if (!where) return null;
	const kind = props.activityType
		? fillUi(ui("blog.block.activityCta.kindWithType", "{type} & activities"), { type: props.activityType })
		: ui("blog.block.activityCta.kind", "activities");
	return (
		<>
			<SaveWidget
				icon="specialdays"
				title={fillUi(ui("blog.block.activityCta.title", "See {kind} in {where}"), { kind, where })}
				body={ui("blog.block.activityCta.body", "Bookable experiences, ready to add straight to your trip.")}
				cta={ui("blog.block.activityCta.cta", "Discover activities")}
				tone="amber"
				onClick={gate.requestPlan}
			/>
			<PlanGateModal gate={gate} title={ui("blog.block.activityCta.modalTitle", "Create a free account to add activities to your trip")} />
		</>
	);
};

const SeasonalCtaBlock = ({ props, data, lang }: BlockProps) => {
	const ui = useGuideUi();
	const { season, destination, photo, href } = props;
	if (!season || !destination || !(photo || data?.photo)) return null;
	return (
		<PhotoCallout
			photo={photo || data?.photo}
			eyebrow={ui("blog.block.seasonalCta.eyebrow", "Timing")}
			title={fillUi(ui("blog.block.seasonalCta.title", "{season} in {destination} is a different season"), { season, destination })}
			body={
				props.body ||
				fillUi(ui("blog.block.seasonalCta.body", "What changes, what it costs and what to book early, the {season} view of {destination}."), {
					season,
					destination,
				})
			}
			ctaLabel={fillUi(ui("blog.block.seasonalCta.cta", "Plan your {season} trip"), { season })}
			ctaHref={localizeHref(href, lang)}
		/>
	);
};

const DoDontBlock = ({ data }: BlockProps) => (data?.etiquette ? <DoDont etiquette={data.etiquette} /> : null);

const WhereToStayBlock = ({ data }: BlockProps) => (data?.guide ? <WhereToStay guide={data.guide} /> : null);

const TrustFooterBlock = ({ props, lang }: BlockProps) => <TrustFooter feedbackHref={localizeHref(props.href, lang)} />;

/** Both props are the block — without either there is no signpost to draw. */
const ReadMoreBlock = ({ props, lang }: BlockProps) =>
	props.href && props.label ? <ReadMore href={localizeHref(props.href, lang)} label={props.label} /> : null;

// ── Wired 2026-09-16 (123yb693pet / 123yb692y1f) ──────────────────────────────
//
// Both were on the "deliberately not wired" list above until Ilse's design landed today.
// Neither needed a new visual language: `hotel-picks` is the hotel half of `TravelWidget`,
// already built and shipping on every `where-to-stay` block and destination page; `explore-more`
// is `ReadMore` repeated under a heading. `save-to-trip` is the one name still missing from
// `BLOCK_COMPONENTS` — out of scope for this ticket, not forgotten.

/**
 * Fold a neighbourhood name to a form two spellings of it can be compared in: accents
 * stripped, `&` spelled out, punctuation collapsed to single spaces. Mirrors `_fold_area()` in
 * `tools/build_guide_documents.py` (the sibling of `canonical_neighbourhood()`, which resolves
 * this same ambiguity server-side for the destination page's own area grid) — reimplemented
 * here rather than shared, because the KB and this frontend do not share code.
 */
const foldAreaName = (s: string) =>
	s
		.normalize("NFD")
		// Combining diacritical marks (U+0300–U+036F) — what NFD splits an accent into.
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.replace(/&/g, " and ")
		.replace(/[^a-z0-9]+/g, " ")
		.trim();

/**
 * Scope a guide's curated hotel picks to one neighbourhood, matched leniently.
 *
 * `hotel-picks` names the area the way a writer would type it — `area="Yaowarat"` — while a
 * hotel carries two spellings of its own: `area` is the curated place's own free-text
 * neighbourhood (e.g. "Banglamphu & Khao San Road"), `neighbourhood` is that resolved against
 * this guide's own neighbourhood list at KB build time (e.g. "Yaowarat (Chinatown)") and is
 * absent when nothing resolved. Neither is guaranteed to equal the prop verbatim, so both are
 * checked, folded, in each direction — same reasoning as `canonical_neighbourhood`'s doc
 * comment, whose two examples are the two cases here.
 *
 * Most areas have no matching hotel — Bangkok curates 3 hotels across 6 areas — and an empty
 * result is correct, not a bug: `StayWidget` (and `TravelWidget` underneath it) already render
 * the booking half on their own when `hotels` is empty (86cba1hzn), so a miss here still leaves
 * the block with something to show.
 */
function hotelsInArea(hotels: CityData["stays"]["hotels"], area: string): CityData["stays"]["hotels"] {
	const wanted = foldAreaName(area);
	if (!wanted) return hotels;
	return hotels.filter((h) =>
		[h.neighbourhood, h.area].some((v) => {
			if (!v) return false;
			const folded = foldAreaName(v);
			return !!folded && (folded === wanted || folded.includes(wanted) || wanted.includes(folded));
		})
	);
}

/**
 * "Our favourite places to stay in {name}" — Ilse's design is exactly the hotel half of
 * `TravelWidget`, already built for `where-to-stay`. Rendered through `StayWidget` rather than
 * `WhereToStay` itself: the article section this sits in already has its own heading, and
 * `WhereToStay` would add a second "Where to stay in {name}" on top of it.
 *
 * `props.area`, when the directive names one, scopes the picks to that neighbourhood
 * (`hotelsInArea` above); without it every curated pick renders, same as `where-to-stay`.
 */
const HotelPicksBlock = ({ props, data }: BlockProps) => {
	const guide = data?.guide as CityData | undefined;
	if (!guide) return null;
	const hotels = props.area ? hotelsInArea(guide.stays.hotels, props.area) : guide.stays.hotels;
	return <StayWidget guide={guide} hotels={hotels} />;
};

/**
 * `explore-more`: a small set of `ReadMore` chips under a heading built from `props.city`.
 * `data.articles` comes pre-resolved — slug and title, nothing else — from
 * `resolve_block_data`'s `explore-more` branch in `tools/build_article_documents.py`; this
 * only ever has to turn each into an internal `/blog/<slug>` href and localise it, the same
 * way `ReadMoreBlock` above does for its own single `href` prop.
 */
const ExploreMoreBlock = ({ props, data, lang }: BlockProps) => {
	const ui = useGuideUi();
	const articles = (data?.articles ?? []) as { slug: string; title: string }[];
	if (!articles.length) return null;
	const heading = props.city
		? fillUi(ui("blog.block.exploreMore.heading", "More on {city}"), { city: props.city })
		: ui("blog.block.exploreMore.headingGeneric", "More to read");
	return (
		<ExploreMore
			heading={heading}
			items={articles.map((a) => ({ href: localizeHref(`/blog/${a.slug}`, lang), label: a.title }))}
		/>
	);
};

/**
 * Every block that has a component. Adding one here and regenerating
 * `tools/block_registry.json` is all it takes for articles to start using it — no
 * exporter change, because the exporter only ever asks "is this name in the registry".
 */
export const BLOCK_COMPONENTS: Record<string, (p: BlockProps) => JSX.Element | null> = {
	"weather-by-month": WeatherBlock,
	"stat-strip": StatStripBlock,
	"editorial-tip": EditorialTipBlock,
	"transport-table": TransportTableBlock,
	"esim-pricing": EsimPricingBlock,
	"ai-planner": AiPlannerBlock,
	"budget-tracker": BudgetTrackerBlock,
	"daily-budget": DailyBudgetBlock,
	"esim-banner": EsimBannerBlock,
	"discover-food": discoverBlock(
		"eatdrink",
		"blog.block.food.title",
		"Discover food spots in Stippl",
		"blog.block.food.body",
		"Find restaurants and markets on the map, save them to your itinerary, works offline too."
	),
	"discover-bars": discoverBlock(
		"eatdrink",
		"blog.block.bars.title",
		"Discover bars in Stippl",
		"blog.block.bars.body",
		"Find rooftop bars and cocktail spots on the map, save them to your itinerary, works offline too."
	),
	"route-cta": RouteCtaBlock,
	"save-poi": SavePoiBlock,
	"activity-cta": ActivityCtaBlock,
	"seasonal-cta": SeasonalCtaBlock,
	"do-dont": DoDontBlock,
	"where-to-stay": WhereToStayBlock,
	"trust-footer": TrustFooterBlock,
	"read-more": ReadMoreBlock,
	"hotel-picks": HotelPicksBlock,
	"explore-more": ExploreMoreBlock,
};

export const IMPLEMENTED_BLOCKS = Object.keys(BLOCK_COMPONENTS).sort();

/**
 * What renders where a block has no component.
 *
 * The prose is `fallbackText` from the exporter, built from the block's own props — never
 * the `CONTENT BLOCK:` editor note, which is a sentence written for Ilse ("Seasonal hero
 * block, links to Thailand hub") and would read as nonsense to a traveller.
 *
 * The warning is deliberately invisible to readers and loud to us: the article is
 * complete and publishable, the *block library* is what is behind.
 */
export function UnimplementedBlock({ block }: { block: ArticleBlock }) {
	useEffect(() => {
		// eslint-disable-next-line no-console
		console.warn(
			`[blog] content block "${block.type}" has no component, rendering fallback text. ` +
				`Add it to BLOCK_COMPONENTS in frontend/src/components/discover/blockRegistry.tsx.`
		);
	}, [block.type]);

	if (!block.fallbackText) return null;
	return (
		<p data-unimplemented-block={block.type} className="article-block-fallback">
			{block.fallbackText}
		</p>
	);
}

/**
 * Records a click on a content block.
 *
 * Exported so `DiscoverArticle` can pass the article's identity in — the block only knows its
 * own type, and "which blocks convert" is only answerable if you can also say on which article.
 */
export function trackBlockClick(blockType: string, article?: { slug?: string; category?: string; geo?: string | null }) {
	trackEvent("blog_block_clicked", { block: blockType, slug: article?.slug, category: article?.category, geo: article?.geo ?? undefined }, "blog");
}

/**
 * Dispatch. Used by `DiscoverArticle` for every `kind: "block"` node.
 *
 * The click listener sits here rather than inside each block, so a block added to
 * `BLOCK_COMPONENTS` is instrumented the day it ships with nothing to remember — the same
 * reasoning `stampClickout` uses inside `trackEvent`. It is a capture-phase listener on a
 * wrapper rather than an `onClick` prop threaded through nineteen components, because most of
 * those render a plain `<a>` and would otherwise each need their own hook.
 */
/**
 * Block types that stay in the catalogue but render nothing (123yb691qvb).
 *
 * The eSIM is a mobile and backend feature — `features/esim.md` lists no web platform — and
 * neither of these could finish what it started on the website. `esim-banner`'s CTA reads "See
 * <place> eSIMs" and pushes `/register`, because `SaveWidget` falls back to the signup route
 * when no `onClick` is given; `esim-pricing` prints package prices out of a collection prod
 * expires every 24 hours.
 *
 * Hidden HERE rather than by deleting the twelve `::block{type="esim-*"}` directives from the
 * articles, for two reasons: the copy is Ilse's and a hidden block is not an editorial mistake
 * to be scrubbed, and dropping them from BLOCK_COMPONENTS would make `renderBlock` treat them
 * as unimplemented and print the directive as plain text, which is louder than the block.
 *
 * Delete a line here to bring one back.
 */
export const HIDDEN_BLOCKS = new Set(["esim-banner", "esim-pricing"]);

export function renderBlock(
	block: ArticleBlock,
	key: string | number,
	article?: { slug?: string; category?: string; geo?: string | null; locale?: string }
) {
	// Before the wrapper, so a hidden block leaves no empty <div data-block> behind.
	if (HIDDEN_BLOCKS.has(block.type)) return null;
	const Component = BLOCK_COMPONENTS[block.type];
	// The article's own locale, not the pathname — this runs during SSR too. A plain `string`
	// off the knowledge-base document, so narrow it before handing it down (same pattern
	// DiscoverArticle/ItineraryArticle use for `articleLang`, see commit c14edf841).
	const lang: MarketingLangCode = article?.locale && isMarketingLangCode(article.locale) ? article.locale : "en";
	const body = !Component || !block.implemented ? <UnimplementedBlock block={block} /> : <Component props={block.props} data={block.data} lang={lang} />;

	return (
		<div
			key={key}
			data-block={block.type}
			onClickCapture={(e) => {
				// Only a real navigation or button press counts — a click on the surrounding
				// padding is not an interaction with the block.
				if ((e.target as HTMLElement)?.closest?.("a,button")) trackBlockClick(block.type, article);
			}}
		>
			{body}
		</div>
	);
}
